<template>
  <div class="address-book-page">
    <div class="address-book-card">
      <el-form :inline="true" :model="query" class="address-book-query" @submit.native.prevent>
        <el-form-item label="地址名称">
          <el-input v-model.trim="query.poiName" clearable placeholder="请输入地址名称" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="省 / 市 / 区"><el-cascader v-model="query.areaCodes" :options="regionOptions" :props="{ checkStrictly: true }" clearable placeholder="请选择地区" @change="search" /></el-form-item>
        <el-form-item label="资料状态"><el-select v-model="query.enabled" clearable placeholder="全部" @change="search"><el-option label="全部" value="" /><el-option label="正常" :value="1" /><el-option label="禁用" :value="0" /></el-select></el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="reset">重置</el-button>
        </el-form-item>
      </el-form>
      <div class="address-book-toolbar">
        <el-button v-permission="'address:add'" type="primary" icon="el-icon-plus" @click="openCreate">新增地址</el-button>
        <el-tooltip content="刷新" placement="top"><el-button icon="el-icon-refresh" @click="fetchList" /></el-tooltip>
      </div>
      <el-table v-loading="loading" :data="list" border stripe>
        <el-table-column prop="poiName" label="地址名称" min-width="150" show-overflow-tooltip />
        <el-table-column label="省 / 市 / 区" min-width="170" show-overflow-tooltip><template slot-scope="{ row }">{{ areaText(row) }}</template></el-table-column>
        <el-table-column prop="address" label="详细地址" min-width="280" show-overflow-tooltip><template slot-scope="{ row }">{{ addressText(row) }}</template></el-table-column>
        <el-table-column label="坐标" min-width="190"><template slot-scope="{ row }">{{ coordinateText(row) }}</template></el-table-column>
        <el-table-column label="联系人" min-width="115"><template slot-scope="{ row }">{{ row.contactName || '-' }}</template></el-table-column>
        <el-table-column label="联系电话" min-width="140"><template slot-scope="{ row }">{{ row.contactPhone || '-' }}</template></el-table-column>
        <el-table-column label="资料状态" width="100" align="center"><template slot-scope="{ row }"><el-tag :type="Number(row.enabled) === 1 ? 'success' : 'info'" size="small">{{ Number(row.enabled) === 1 ? '正常' : '禁用' }}</el-tag></template></el-table-column>
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button v-permission="'address:edit'" type="text" size="small" @click="openEdit(row)">编辑</el-button>
            <el-button v-permission="'address:edit'" type="text" size="small" @click="changeStatus(row)">{{ Number(row.enabled) === 1 ? '禁用' : '启用' }}</el-button>
            <el-button v-permission="'address:delete'" type="text" size="small" class="address-book-delete" @click="remove(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="address-book-pagination"
        background
        layout="total, prev, pager, next, sizes"
        :current-page="page"
        :page-size="limit"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @current-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>
    <address-book-drawer :visible.sync="drawerVisible" :address-book="currentAddressBook" @saved="onSaved" />
  </div>
</template>

<script>
import { regionData } from 'element-china-area-data'
import { normalizeAreaCodes, getAreaName, getAreaCodesByAddress, stripAreaPrefix } from './locationArea'
import { deleteAddressBook, getAddressBookList, updateAddressBookStatus, isRequestSuccess } from './lingdongApi'
import AddressBookDrawer from './AddressBookDrawer.vue'
import { formatLonLat, wgs84LonLatToGcj02Point } from './coordinate'

const initialQuery = () => ({ poiName: '', areaCodes: [], enabled: 1 })

export default {
  name: 'AddressBookManage',
  components: { AddressBookDrawer },
  data() {
    return { loading: false, requestId: 0, list: [], query: initialQuery(), regionOptions: regionData, page: 1, limit: 20, total: 0, drawerVisible: false, currentAddressBook: null }
  },
  created() { this.fetchList() },
  beforeDestroy() { this.requestId += 1 },
  methods: {
    areaText(row) {
      let areaCodes = normalizeAreaCodes(row.areaCodes)
      if (areaCodes.length !== 3) {
        const nameCodes = getAreaCodesByAddress(row.areaName)
        const addressCodes = nameCodes.length ? nameCodes : getAreaCodesByAddress(row.address)
        if (addressCodes.length) areaCodes = addressCodes
      }
      return getAreaName(areaCodes) || row.areaName || '-'
    },
    addressText(row) {
      const areaName = this.areaText(row)
      return stripAreaPrefix(row.address, areaName === '-' ? '' : areaName)
    },
    coordinateText(row) {
      return row.longitude === null || row.longitude === undefined || row.latitude === null || row.latitude === undefined ? '-' : formatLonLat(wgs84LonLatToGcj02Point(`${row.longitude},${row.latitude}`)) || '-'
    },
    async fetchList() {
      const requestId = ++this.requestId
      this.loading = true
      try {
        const areaCodes = normalizeAreaCodes(this.query.areaCodes)
        const params = { poiName: this.query.poiName, page: this.page, limit: this.limit }
        if (areaCodes.length) params.areaCode = areaCodes[areaCodes.length - 1]
        if (this.query.enabled !== '' && this.query.enabled !== null && this.query.enabled !== undefined) params.enabled = this.query.enabled
        const res = await getAddressBookList(params)
        if (requestId !== this.requestId) return
        if (!isRequestSuccess(res)) throw new Error(res.msg || '查询失败')
        const data = res.data || {}
        this.list = data.items || []
        this.total = Number(data.total) || 0
        const maxPage = Math.max(1, Math.ceil(this.total / this.limit))
        if (this.page > maxPage) { this.page = maxPage; return this.fetchList() }
      } catch (e) {
        if (requestId !== this.requestId) return
        this.list = []
        this.total = 0
        this.$message.error(e.msg || e.message || '查询失败')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    search() { this.page = 1; this.fetchList() },
    reset() { this.query = initialQuery(); this.search() },
    onPageChange(page) { this.page = page; this.fetchList() },
    onSizeChange(limit) { this.limit = limit; this.page = 1; this.fetchList() },
    openCreate() { this.currentAddressBook = null; this.drawerVisible = true },
    openEdit(row) { this.currentAddressBook = { ...row }; this.drawerVisible = true },
    onSaved() { this.fetchList() },
    async changeStatus(row) {
      const enabled = Number(row.enabled) === 1 ? 0 : 1
      const action = enabled === 1 ? '启用' : '禁用'
      try {
        await this.$confirm(`确认${action}地址“${row.poiName}”吗？`, '提示', { type: 'warning' })
        const res = await updateAddressBookStatus(row.id, enabled)
        if (!isRequestSuccess(res)) throw new Error(res.msg || `${action}失败`)
        this.$message.success(`地址已${action}`)
        this.fetchList()
      } catch (e) {
        if (e && e !== 'cancel' && e !== 'close') this.$message.error(e.msg || e.message || `${action}失败`)
      }
    },
    remove(row) {
      this.$confirm(`确认删除地址“${row.poiName}”吗？`, '提示', { type: 'warning' }).then(async () => {
        const res = await deleteAddressBook(row.id)
        if (!isRequestSuccess(res)) throw new Error(res.msg || '删除失败')
        this.$message.success('地址已删除')
        if (this.list.length === 1 && this.page > 1) this.page -= 1
        this.fetchList()
      }).catch((e) => {
        if (e && e !== 'cancel' && e !== 'close') this.$message.error(e.msg || e.message || '删除失败')
      })
    }
  }
}
</script>

<style scoped>
.address-book-page { width: 100%; min-height: 100%; padding: 16px; box-sizing: border-box; background: #f0f2f5; }
.address-book-card { min-height: 100%; padding: 16px; border-radius: 4px; background: #fff; box-sizing: border-box; }
.address-book-query { margin-bottom: 4px; }
.address-book-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.address-book-pagination { margin-top: 16px; text-align: right; }
.address-book-delete { color: #e64545; }
</style>
